import { AnimatePresence, motion, type Variants } from "motion/react";
import { useTranslations } from "next-intl";
import {
  type ComponentPropsWithoutRef,
  type MouseEvent,
  useState,
} from "react";
import { toast } from "sonner";
import { HeroIcon } from "@/components/hero-icon";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type InstructionCardProps = Omit<
  ComponentPropsWithoutRef<"button">,
  "children" | "title"
> & {
  title: string;
  value: string | undefined;
  loading?: boolean;
  copyable?: boolean;
  skeletonClassName?: string;
};

const copyVariants: Variants = {
  initial: { opacity: 0, transform: "scale(0.5)", filter: "blur(4px)" },
  animate: { opacity: 1, transform: "scale(1)", filter: "blur(0px)" },
  exit: { opacity: 0, transform: "scale(0.5)", filter: "blur(4px)" },
};

export const InstructionCard = ({
  className,
  title,
  value,
  copyable = false,
  skeletonClassName,
  onClick,
  disabled,
  loading,
  ...props
}: InstructionCardProps) => {
  const t = useTranslations("common");
  const [copied, setCopied] = useState(false);
  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    if (copyable && value !== undefined) {
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 1000);
      } catch {
        toast.error(t("clipboardError"));
      }
    }
    onClick?.(event);
  };

  return (
    <button
      type="button"
      className={cn(
        "group flex w-full items-center self-center rounded-lg border border-border bg-background px-4 py-2 text-start font-semibold",
        copyable && "cursor-pointer",
        className,
      )}
      onClick={handleClick}
      disabled={disabled ?? loading}
      {...props}
    >
      <div className="flex-1">
        <InstructionCardTitle>{title}</InstructionCardTitle>
        {loading ? (
          <Skeleton className={cn("h-5 w-32", skeletonClassName)} />
        ) : (
          value
        )}
      </div>
      <div className="shrink-0">
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-top shadow-md transition-shadow duration-150 group-hover:shadow-lg">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={copied ? "cb-copied" : "cb-idle"}
              variants={copyVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.1 }}
            >
              {copied ? (
                <HeroIcon name="check" className="h-4 w-4" iconStyle="micro" />
              ) : (
                <HeroIcon
                  name="square-2-stack"
                  className="h-4 w-4"
                  iconStyle="micro"
                />
              )}
            </motion.span>
          </AnimatePresence>
        </div>
      </div>
    </button>
  );
};

export const InstructionCardTitle = ({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<"p">) => (
  <p className={cn("font-medium text-muted text-sm", className)} {...props}>
    {children}
  </p>
);
